/**
 * Helpers compartidos entre los subcomponentes del expediente.
 *
 * Sprint 18.4.2 (P1-6 audit, mayo 20, 2026): extraído del antiguo
 * `ExpedienteDetail.tsx` (1282 LOC) que mezclaba el orchestrator con
 * todos sus subcomponentes + helpers privados.
 */

import { sectorLabel, type ProjectDetailDTO } from "@/lib/api";

/**
 * Texto del chip de sector. Los expedientes idea-first nacen con
 * `sector = 'generico'` hasta que la formulacion fija el real; pintarlo tal
 * cual mostraba "GENERICO" como si fuera un sector (issue #572). Los demas
 * van con su rotulo, no con la clave (issue #880).
 */
export function etiquetaSector(sector: string): string {
  return sector === "generico" ? "Sin sector aún" : sectorLabel(sector);
}

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * El PDF solo aporta valor si hay algo que mostrar (Sprint 11 gate).
 * Habilitamos exportar cuando el expediente tiene CUALQUIERA de:
 * - status distinto a `draft` (ya avanzó),
 * - al menos 1 item del checklist completado,
 * - al menos 1 documento adjunto,
 * - BPIN registrado.
 */
export function isExpedienteExportable(project: ProjectDetailDTO): boolean {
  if (project.status !== "draft") return true;
  if (project.bpin) return true;
  if (project.checklist.some((item) => item.is_completed)) return true;
  if (project.checklist.some((item) => item.linked_document_id !== null)) return true;
  return false;
}

/**
 * Por que no se puede exportar todavia, en pasos que el usuario SI puede
 * dar. `undefined` cuando ya es exportable.
 *
 * Issue #569: los expedientes idea-first no tienen checklist por diseno;
 * pedirles "completa un item, adjunta un doc" era una guia imposible. Sin
 * checklist, lo que desbloquea el PDF es radicar o registrar el BPIN.
 */
export function exportDisabledReason(project: ProjectDetailDTO): string | undefined {
  if (isExpedienteExportable(project)) return undefined;
  if (project.checklist.length === 0) {
    return "Avanza el expediente para exportar (márcalo como radicado o registra el BPIN).";
  }
  return "Avanza el expediente para exportar (completa un item, adjunta un doc o registra el BPIN).";
}
